/**
 * Secure token storage abstraction.
 *
 * Provides a Keychain/Keystore-ready interface for sensitive auth data.
 * On native, sensitive tokens (vrs_auth_token, vrs_refresh_token) should be stored via
 * react-native-keychain when it is linked. We mirror values into the existing
 * persistent cache so synchronous call sites can still read during app boot.
 */
import { getPersistentItem, getPersistentItemAsync, getStorageGeneration, isPersistentItemBlocked, removePersistentItem, removePersistentItemConfirmed, setPersistentItem } from './storage';
const SECURE_KEYS = new Set([
    'vrs_auth_token',
    'vrs_refresh_token',
    'vrs_interpreter_auth'
]);
const KEYCHAIN_SERVICE_PREFIX = 'malka.vrs.';
let keychainModule;
const keychainWrites = new Map();
function serializeKeychain(key, operation) {
    const pending = (keychainWrites.get(key) || Promise.resolve()).catch(() => undefined).then(operation);
    keychainWrites.set(key, pending);
    void pending.finally(() => {
        if (keychainWrites.get(key) === pending)
            keychainWrites.delete(key);
    }).catch(() => undefined);
    return pending;
}
/**
 * react-native-keychain is now a real dependency, so it can be required — but
 * only on native. On web the module does not exist, and requiring it there
 * would break the browser bundle; web keeps using persistent storage, which is
 * the browser's own origin-scoped storage and is the correct answer there.
 *
 * Resolved once and cached: this is called on every sensitive read and write.
 */
function getKeychain() {
    if (keychainModule !== undefined) {
        return keychainModule;
    }
    const isNative = typeof navigator !== 'undefined'
        && navigator.product === 'ReactNative';
    if (!isNative) {
        keychainModule = null;
        return keychainModule;
    }
    try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
        keychainModule = require('react-native-keychain');
    }
    catch {
        // A build that somehow lacks the native module must degrade to the
        // mirror rather than take the app down at boot.
        keychainModule = null;
    }
    return keychainModule;
}
function serviceFor(key) {
    return `${KEYCHAIN_SERVICE_PREFIX}${key}`;
}
/**
 * Read a sensitive value. Sync version checks the in-memory/localStorage
 * cache first; use getSecureItemAsync for the authoritative native read.
 */
export function getSecureItem(key) {
    if (!SECURE_KEYS.has(key)) {
        return getPersistentItem(key);
    }
    return getPersistentItem(key);
}
/**
 * Async read for sensitive values — preferred on native.
 */
export async function getSecureItemAsync(key) {
    if (isPersistentItemBlocked(key))
        return null;
    const generation = getStorageGeneration();
    if (!SECURE_KEYS.has(key)) {
        return getPersistentItemAsync(key);
    }
    const keychain = getKeychain();
    if (keychain?.getGenericPassword) {
        const credentials = await keychain.getGenericPassword({ service: serviceFor(key) });
        if (isPersistentItemBlocked(key) || generation !== getStorageGeneration())
            return null;
        if (credentials && credentials.password) {
            setPersistentItem(key, credentials.password);
            return credentials.password;
        }
    }
    return getPersistentItemAsync(key);
}
/**
 * Write a sensitive value.
 */
export function setSecureItem(key, value) {
    if (isPersistentItemBlocked(key))
        return;
    if (!SECURE_KEYS.has(key)) {
        setPersistentItem(key, value);
        return;
    }
    setPersistentItem(key, value);
    const keychain = getKeychain();
    if (keychain?.setGenericPassword) {
        const generation = getStorageGeneration();
        void serializeKeychain(key, async () => {
            if (isPersistentItemBlocked(key) || generation !== getStorageGeneration())
                return;
            await keychain.setGenericPassword?.(key, value, { service: serviceFor(key) });
        }).catch(() => console.warn('Secure session write was not confirmed.'));
    }
}
/**
 * Remove a sensitive value.
 */
export function removeSecureItem(key) {
    if (!SECURE_KEYS.has(key)) {
        removePersistentItem(key);
        return;
    }
    removePersistentItem(key);
    const keychain = getKeychain();
    if (keychain?.resetGenericPassword) {
        void serializeKeychain(key, async () => {
            await keychain.resetGenericPassword?.({ service: serviceFor(key) });
        }).catch(() => console.warn('Secure session removal was not confirmed.'));
    }
}
function requiredKeychain() {
    const keychain = getKeychain();
    if (!keychain?.getGenericPassword || !keychain.setGenericPassword || !keychain.resetGenericPassword) {
        throw new Error('Secure session storage is unavailable.');
    }
    return { get: keychain.getGenericPassword.bind(keychain), set: keychain.setGenericPassword.bind(keychain),
        remove: keychain.resetGenericPassword.bind(keychain) };
}
export async function removeSecureItemConfirmed(key) {
    const keychain = requiredKeychain();
    await serializeKeychain(key, async () => {
        await keychain.remove({ service: serviceFor(key) });
        if (await keychain.get({ service: serviceFor(key) }))
            throw new Error('Secure session removal was not confirmed.');
    });
    await removePersistentItemConfirmed(key);
}
/**
 * Keychain/Keystore only, with no plaintext mirror in persistent storage
 * (unlike the account token above): a separate credential, such as the Jobs
 * buyer session (`vri_job_client_session`). Each call throws when native
 * secure storage is unavailable; callers treat that as "not saved".
 */
export async function readSecureOnlyItem(key) {
    const keychain = requiredKeychain();
    const saved = await keychain.get({ service: serviceFor(key) });
    return saved ? saved.password : null;
}
/** Saved and read back; a write that finishes after sign-out began is erased. */
export async function writeSecureOnlyItem(key, value) {
    const keychain = requiredKeychain();
    const generation = getStorageGeneration();
    const accessible = getKeychain()?.ACCESSIBLE?.WHEN_UNLOCKED_THIS_DEVICE_ONLY;
    await serializeKeychain(key, async () => {
        await keychain.set(key, value, { service: serviceFor(key), ...(accessible ? { accessible } : {}) });
        const saved = await keychain.get({ service: serviceFor(key) });
        if (!saved || saved.password !== value || generation !== getStorageGeneration()) {
            await keychain.remove({ service: serviceFor(key) });
            throw new Error('Secure value was not saved.');
        }
    });
}
export async function removeSecureOnlyItem(key) {
    const keychain = requiredKeychain();
    await serializeKeychain(key, async () => {
        await keychain.remove({ service: serviceFor(key) });
        if (await keychain.get({ service: serviceFor(key) }))
            throw new Error('Secure value removal was not confirmed.');
    });
}
// The original logout credential must never be copied to the plaintext mirror.
const SIGN_OUT_SERVICE = 'malka.vrs.pending-sign-out.v1';
export async function readPendingSecureSignOut() {
    const value = await requiredKeychain().get({ service: SIGN_OUT_SERVICE });
    return value ? value.password : null;
}
export async function writePendingSecureSignOut(value) {
    await requiredKeychain().set('pending-sign-out', value, { service: SIGN_OUT_SERVICE });
    if (await readPendingSecureSignOut() !== value)
        throw new Error('Sign-out recovery was not saved.');
}
export async function removePendingSecureSignOut() {
    await requiredKeychain().remove({ service: SIGN_OUT_SERVICE });
    if (await readPendingSecureSignOut() !== null)
        throw new Error('Sign-out recovery removal was not confirmed.');
}
/**
 * Whether hardware-backed storage is actually in use.
 *
 * True only on native with the Keychain module resolved. Web and any build
 * missing the native module answer false, which is the honest answer: the
 * mirror in persistent storage is all that holds the token there.
 */
export async function isSecureStorageAvailable() {
    const keychain = getKeychain();
    return Boolean(keychain?.setGenericPassword);
}
